/**
 * DtcLookupCard.tsx — DTC translation lookup panel for work orders.
 *
 * Input: DTC code (validated ^[PBCU][0-9]{4}$, uppercase-forced)
 * → "Translate" → result panel (system / definition / likely_causes)
 * → related-procedure chips (open ProcedurePicker pre-selected)
 * → "Add to work order" (attach DTC to WO)
 *
 * Degraded/budget(402)/503 handling mirrors AiLookupPanel.
 *
 * Feature-gated: rendered only when the shop has DTC diagnosis enabled (useAiFeatureFlags, migration 195 — owner-controlled in AI settings).
 */

import React, { useState, useCallback } from 'react';
import {
  Card,
  Input,
  Button,
  Alert,
  Spin,
  Space,
  Tag,
  Typography,
  Result,
} from 'antd';
import { SearchOutlined, PlusOutlined } from '@ant-design/icons';
import { useDtc } from '../../hooks/useDtc';
import type { DtcTranslateResult } from '../../services/dtcService';
import type { VehicleSnapshot } from '../../services/diagnosticService';
import { ProcedurePicker } from '../Diagnostics/ProcedurePicker';
import type { Procedure } from '../../services/diagnosticService';

const { Text } = Typography;

/** SAE J2012 generic + manufacturer code shape */
const DTC_REGEX = /^[PBCU][0-9]{4}$/;

export interface DtcLookupCardProps {
  workOrderId: number;
  vehicle?: VehicleSnapshot;
  /** Called after a DTC is successfully attached to the WO */
  onAttached?: () => void;
}

const DtcLookupCard: React.FC<DtcLookupCardProps> = ({
  workOrderId,
  vehicle,
  onAttached,
}) => {
  const [codeInput, setCodeInput] = useState('');
  const [inputError, setInputError] = useState<string | null>(null);
  const [attaching, setAttaching] = useState(false);
  const [attachSuccess, setAttachSuccess] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  // We don't pre-select a specific procedure slug in ProcedurePicker (the component
  // doesn't expose a controlled selection prop); instead we open it so the tech can
  // pick from the filtered list.  The suggested slugs are displayed as informational chips.

  const { translation, translate, attach } = useDtc();

  // ── Input handling ──────────────────────────────────────────────────────────

  const handleCodeChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const upper = e.target.value.toUpperCase();
    setCodeInput(upper);
    setInputError(null);
    setAttachSuccess(false);
  }, []);

  const handleTranslate = useCallback(async () => {
    const code = codeInput.trim().toUpperCase();
    if (!DTC_REGEX.test(code)) {
      setInputError('Enter a valid DTC code (e.g. P0301)');
      return;
    }
    setAttachSuccess(false);
    await translate({
      code,
      vehicleSnapshot: vehicle
        ? {
            year: vehicle.year,
            make: vehicle.make,
            model: vehicle.model,
            // Backend VehicleSnapshotSchema is .strict() and expects `engine`.
            // Only include it when present so the strict schema doesn't reject undefined.
            ...(vehicle.engine_size ? { engine: vehicle.engine_size } : {}),
          }
        : undefined,
    });
  }, [codeInput, vehicle, translate]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter') handleTranslate();
    },
    [handleTranslate]
  );

  // ── Attach to work order ────────────────────────────────────────────────────

  const handleAttach = useCallback(async () => {
    if (!translation.data) return;
    setAttaching(true);
    try {
      await attach(workOrderId, translation.data);
      setAttachSuccess(true);
      onAttached?.();
    } catch {
      // Attach failed — do NOT show the success tag.
      setAttachSuccess(false);
    } finally {
      setAttaching(false);
    }
  }, [translation.data, workOrderId, attach, onAttached]);

  // ── Render helpers ──────────────────────────────────────────────────────────

  const result = translation.data as (DtcTranslateResult & {
    vendor_degraded?: boolean;
    cached?: boolean;
  }) | null;

  const budgetExceeded =
    translation.errorStatus === 402
      ? (translation.error as string | null)
      : null;
  const serviceUnavailable = translation.errorStatus === 503;

  const vendorDegraded = result?.vendor_degraded ?? false;
  const isCached = result?.cached ?? false;

  const handleProcedurePick = useCallback((_p: Procedure) => {
    setPickerOpen(false);
  }, []);

  return (
    <Card
      size="small"
      title={<Text strong>DTC Lookup</Text>}
      style={{ marginTop: 8 }}
      data-testid="dtc-lookup-card"
    >
      {/* Budget-exceeded Result card (mirrors AiLookupPanel Gap 3) */}
      {budgetExceeded && (
        <div data-testid="dtc-budget-exceeded-card">
          <Result
            status="warning"
            title="AI lookups paused — monthly budget exceeded"
            subTitle="Contact owner to raise the budget or wait for the next billing cycle."
            style={{ padding: '12px 0' }}
          />
        </div>
      )}

      {/* Service unavailable banner */}
      {serviceUnavailable && (
        <div data-testid="dtc-service-unavailable-banner">
          <Alert
            type="warning"
            showIcon
            message="DTC lookup temporarily unavailable"
            description="The AI service is currently unavailable. Try again shortly."
            style={{ marginBottom: 8 }}
          />
        </div>
      )}

      {/* Degraded / cached result banner (mirrors AiLookupPanel Gap 4) */}
      {(vendorDegraded || isCached) && (
        <Alert
          type="warning"
          showIcon
          message="Showing cached result — vendor temporarily unavailable"
          description="These results may be stale. The system will auto-recover when the vendor comes back online."
          style={{ marginBottom: 8 }}
          data-testid="dtc-vendor-degraded-banner"
        />
      )}

      {/* Input row */}
      <Space.Compact style={{ width: '100%', marginBottom: inputError ? 4 : 12 }}>
        <Input
          placeholder="P0301"
          value={codeInput}
          onChange={handleCodeChange}
          onKeyDown={handleKeyDown}
          maxLength={5}
          style={{ textTransform: 'uppercase' }}
          status={inputError ? 'error' : undefined}
          data-testid="dtc-code-input"
        />
        <Button
          type="primary"
          icon={<SearchOutlined />}
          loading={translation.loading}
          disabled={!codeInput.trim()}
          onClick={handleTranslate}
          data-testid="dtc-translate-btn"
        >
          Translate
        </Button>
      </Space.Compact>

      {inputError && (
        <Text type="danger" style={{ fontSize: 12, display: 'block', marginBottom: 8 }} data-testid="dtc-input-error">
          {inputError}
        </Text>
      )}

      {translation.loading && (
        <Spin style={{ display: 'block', margin: '16px auto' }} />
      )}

      {/* Translation error (non-budget, non-503) */}
      {translation.error && !budgetExceeded && !serviceUnavailable && (
        <Alert
          type="error"
          message={translation.error}
          style={{ marginBottom: 8 }}
          data-testid="dtc-translate-error"
        />
      )}

      {/* Result panel */}
      {!translation.loading && result && (
        <div data-testid="dtc-result-panel">
          <Space direction="vertical" style={{ width: '100%' }} size="small">
            <Space wrap>
              <Tag color="blue" data-testid="dtc-result-code">{result.code}</Tag>
              {result.system && (
                <Tag color="purple" data-testid="dtc-result-system">{result.system}</Tag>
              )}
              <Tag color="default">{result.source}</Tag>
            </Space>

            <Text strong style={{ fontSize: 14 }} data-testid="dtc-result-definition">
              {result.definition}
            </Text>

            {result.likely_causes && result.likely_causes.length > 0 && (
              <div>
                <Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 4 }}>
                  Likely causes:
                </Text>
                <ul style={{ margin: '0 0 0 16px', padding: 0, fontSize: 13 }} data-testid="dtc-likely-causes">
                  {result.likely_causes.map((cause, i) => (
                    <li key={i}>{cause}</li>
                  ))}
                </ul>
              </div>
            )}

            {/* Related procedure suggestion chips */}
            {result.related_procedures && result.related_procedures.length > 0 && (
              <div>
                <Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 4 }}>
                  Suggested procedures:
                </Text>
                <Space wrap>
                  {result.related_procedures.map((slug) => (
                    <Tag
                      key={slug}
                      color="cyan"
                      style={{ cursor: 'pointer' }}
                      onClick={() => setPickerOpen(true)}
                      data-testid={`dtc-procedure-chip-${slug}`}
                    >
                      {slug}
                    </Tag>
                  ))}
                </Space>
              </div>
            )}

            {result.attribution && (
              <Text type="secondary" style={{ fontSize: 11 }} data-testid="dtc-attribution">
                {result.attribution}
              </Text>
            )}

            {attachSuccess ? (
              <Tag color="success" data-testid="dtc-attach-success">
                Added to work order
              </Tag>
            ) : (
              <Button
                type="primary"
                size="small"
                icon={<PlusOutlined />}
                loading={attaching}
                onClick={handleAttach}
                data-testid="dtc-attach-btn"
              >
                Add to work order
              </Button>
            )}
          </Space>
        </div>
      )}

      {/* Procedure picker modal */}
      <ProcedurePicker
        open={pickerOpen}
        vehicle={vehicle}
        onPick={handleProcedurePick}
        onClose={() => setPickerOpen(false)}
      />
    </Card>
  );
};

export default DtcLookupCard;
